import {ReactElement, useEffect, useMemo, useState} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {ChevronRightIcon} from 'components'
import {
  DopamineProgressItem,
  DopamineRunProgress,
} from './DopamineAssistant.types'
import './DopamineAssistantProgress.css'

type DopamineAssistantProgressProps = {
  progress: DopamineRunProgress
}

const getElapsedSeconds = (
  startedAt: number | null,
  endedAt: number | null,
  now: number,
): number => {
  if (startedAt === null) return 0
  const elapsedMilliseconds = (endedAt ?? now) - startedAt
  return Math.max(0, Math.floor(elapsedMilliseconds / 1000))
}

export const DopamineAssistantProgress = ({
  progress,
}: DopamineAssistantProgressProps): ReactElement | null => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const [now, setNow] = useState(Date.now)
  const [showCompletedItems, setShowCompletedItems] = useState(false)

  useEffect(() => {
    if (progress.status !== 'active') return undefined

    setNow(Date.now())
    const intervalId = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(intervalId)
  }, [progress.status, progress.startedAt])

  useEffect(() => {
    setShowCompletedItems(false)
  }, [progress.runId])

  const elapsedSeconds = useMemo(
    () => getElapsedSeconds(progress.startedAt, progress.endedAt, now),
    [now, progress.endedAt, progress.startedAt],
  )
  const completedSummary = t(
    progress.startedAt === null ? 'RAN_STEPS' : 'RAN_STEPS_WITH_DURATION',
    {
      count: progress.items.length,
      seconds: elapsedSeconds,
    },
  )
  const completedToggleAction = t(
    showCompletedItems ? 'COLLAPSE_ALL' : 'EXPAND_ALL',
  )

  if (progress.status === 'idle') return null

  const visibleItems: DopamineProgressItem[] =
    progress.status === 'active' && progress.items.length === 0
      ? [
          {
            activeLabel: t('INVESTIGATING'),
            completedLabel: t('INVESTIGATING'),
            id: 'initial-investigation',
            kind: 'step',
            status: 'active',
          },
        ]
      : progress.items
  const activeIndex = visibleItems.findIndex(item => item.status === 'active')
  const progressItems = (
    <ol aria-label={t('INVESTIGATION_PROGRESS')}>
      {visibleItems.map((item, index) => {
        const isActive =
          progress.status === 'active' && item.status === 'active'
        const isFeed = progress.status === 'active' && index === activeIndex - 1
        let itemLabel = item.completedLabel

        if (item.status === 'active') itemLabel = item.activeLabel
        if (item.status === 'failed') {
          itemLabel = t('PROGRESS_DID_NOT_FINISH', {label: item.activeLabel})
        }

        return (
          <li
            aria-current={isActive ? 'step' : undefined}
            className={classNames(
              'dopamine-progress-item text-dopamine-accent',
              isActive && 'dopamine-progress-item-active',
              isFeed && 'dopamine-progress-item-feed',
            )}
            key={item.id}
          >
            <span aria-hidden="true" className="dopamine-progress-node" />
            <span
              className={classNames(
                'dopamine-progress-label block leading-5',
                isActive ? 'dopamine-progress-shimmer' : 'text-white/50',
              )}
            >
              {itemLabel}
            </span>
          </li>
        )
      })}
    </ol>
  )

  return (
    <>
      {progress.status === 'completed' ? (
        <div className="mb-3">
          <button
            aria-expanded={showCompletedItems}
            aria-label={`${completedSummary}. ${completedToggleAction}`}
            className="dopamine-progress-toggle"
            data-cy="dopamine-assistant-progress-toggle"
            type="button"
            onClick={() => setShowCompletedItems(current => !current)}
          >
            <span>{completedSummary}</span>
            <ChevronRightIcon
              classes={classNames(
                'transition-transform duration-base',
                showCompletedItems ? '-rotate-90' : 'rotate-90',
              )}
              height={10}
              strokeColor="currentColor"
              strokeWidth={1.5}
              width={6}
            />
          </button>
          {showCompletedItems ? (
            <div className="mt-2.5">{progressItems}</div>
          ) : null}
        </div>
      ) : (
        <div>
          {progressItems}
          {progress.status === 'failed' ? (
            <p
              className="ml-5.5 mt-3 text-11px text-dopamine-muted"
              role="alert"
            >
              {progress.failureMessage}
            </p>
          ) : (
            <>
              <p className="ml-5.5 mt-3 text-11px tabular-nums text-dopamine-muted">
                {t('ELAPSED_SECONDS', {seconds: elapsedSeconds})}
              </p>
              <output className="sr-only">{t('INVESTIGATING')}</output>
            </>
          )}
        </div>
      )}
    </>
  )
}
